/**
 * Cost / yield / acreage entry form (Req 2.1, 2.5, 2.6, 2.7, 2.8).
 *
 * Per crop, provides fields for Actual Costs (with a per-acre vs whole-farm
 * toggle), Expected Yield, and Planted Acres. `GET /api/crops/{crop}` prefills
 * the current stored values (Req 2.9); `PUT /api/crops/{crop}` saves. Inline
 * validation messages mirror the server's field errors: a rejected submission
 * shows the offending field's message and leaves the stored value unchanged
 * (Req 2.6, 2.7, 2.8) — the server remains authoritative.
 */
import type { ApiClient, CropInputsBody } from "../api/client";
import { RANGES, cropLabel, type Crop } from "../config";
import type { CostMode, FieldError } from "../api/types";
import { el } from "../ui/dom";

/** Map a server field name to the local error slot that displays it. */
function slotForField(field: string): "cost" | "expectedYield" | "plantedAcres" | "general" {
  switch (field) {
    case "actualCosts":
    case "costValue":
      return "cost";
    case "expectedYield":
      return "expectedYield";
    case "plantedAcres":
      return "plantedAcres";
    default:
      return "general";
  }
}

/** Build the cost/yield/acreage form view for one crop. */
export function cropFormView(api: ApiClient, crop: Crop): HTMLElement {
  const errors = {
    cost: el("p", { class: "field-error", attrs: { role: "alert" } }),
    expectedYield: el("p", { class: "field-error", attrs: { role: "alert" } }),
    plantedAcres: el("p", { class: "field-error", attrs: { role: "alert" } }),
    general: el("p", { class: "form-message", attrs: { role: "alert", "aria-live": "polite" } }),
  };

  const costModeSelect = el("select", {
    class: "field-input",
    name: "costMode",
    attrs: { "aria-label": "Cost basis" },
  }) as HTMLSelectElement;
  costModeSelect.append(
    el("option", { value: "per_acre", text: "Per acre" }),
    el("option", { value: "whole_farm", text: "Whole-farm total" })
  );

  const costInput = numberInput("costValue", "Actual costs", RANGES.actualCosts.min, RANGES.actualCosts.max);
  const yieldInput = numberInput("expectedYield", "Expected yield (bu/acre)", RANGES.expectedYield.min, RANGES.expectedYield.max);
  const acresInput = numberInput("plantedAcres", "Planted acres", RANGES.plantedAcres.min, RANGES.plantedAcres.max);

  const save = el("button", { type: "submit", class: "btn btn-primary", text: "Save" });

  let submitting = false;

  const clearErrors = (): void => {
    errors.cost.textContent = "";
    errors.expectedYield.textContent = "";
    errors.plantedAcres.textContent = "";
    errors.general.textContent = "";
    errors.general.className = "form-message";
  };

  const showFieldErrors = (fieldErrors: FieldError[]): void => {
    for (const fe of fieldErrors) {
      const slot = slotForField(fe.field);
      errors[slot].textContent = fe.message;
    }
  };

  const costLabel = el("label", { class: "field-label", text: "Actual costs" });

  const form = el(
    "form",
    {
      class: "entry-form",
      on: {
        submit: (event: Event) => {
          event.preventDefault();
          void submit();
        },
      },
    },
    [
      field("Cost basis", costModeSelect),
      el("div", { class: "field" }, [costLabel, costInput, errors.cost]),
      el("div", { class: "field" }, [
        el("label", { class: "field-label", text: "Expected yield (bu/acre)" }),
        yieldInput,
        errors.expectedYield,
      ]),
      el("div", { class: "field" }, [
        el("label", { class: "field-label", text: "Planted acres" }),
        acresInput,
        errors.plantedAcres,
      ]),
      save,
      errors.general,
    ]
  );

  const root = el("section", { class: "view crop-form-view" }, [
    el("h2", { class: "view-title", text: `${cropLabel(crop)} · costs & yield` }),
    form,
  ]);

  // Update the cost field hint when the basis changes.
  costModeSelect.addEventListener("change", () => {
    costLabel.textContent =
      costModeSelect.value === "whole_farm" ? "Actual costs (whole-farm total)" : "Actual costs (per acre)";
  });

  void prefill();

  async function prefill(): Promise<void> {
    const result = await api.getCrop(crop);
    if (result.ok && !("exists" in result.data)) {
      const stored = result.data;
      costModeSelect.value = stored.costMode;
      costLabel.textContent =
        stored.costMode === "whole_farm" ? "Actual costs (whole-farm total)" : "Actual costs (per acre)";
      costInput.value = String(stored.costValue);
      yieldInput.value = String(stored.expectedYield);
      if (stored.plantedAcres !== undefined) {
        acresInput.value = String(stored.plantedAcres);
      }
    }
  }

  async function submit(): Promise<void> {
    if (submitting) return;
    clearErrors();

    const costMode = costModeSelect.value as CostMode;
    const costValue = parseNumber(costInput.value);
    const expectedYield = parseNumber(yieldInput.value);
    const plantedAcres = acresInput.value.trim().length > 0 ? parseNumber(acresInput.value) : undefined;

    // Light client-side mirror of the server rules (server stays authoritative).
    let hasClientError = false;
    if (costValue === null) {
      errors.cost.textContent = `${RANGES.actualCosts.label} must be a number.`;
      hasClientError = true;
    }
    if (expectedYield === null) {
      errors.expectedYield.textContent = `${RANGES.expectedYield.label} must be a number.`;
      hasClientError = true;
    } else if (expectedYield <= 0) {
      errors.expectedYield.textContent = "Expected Yield must be greater than zero.";
      hasClientError = true;
    }
    if (acresInput.value.trim().length > 0 && plantedAcres === null) {
      errors.plantedAcres.textContent = `${RANGES.plantedAcres.label} must be a number.`;
      hasClientError = true;
    }
    if (costMode === "whole_farm" && plantedAcres === undefined) {
      errors.plantedAcres.textContent =
        "Planted Acres is required before entering a whole-farm total cost.";
      hasClientError = true;
    }
    if (hasClientError || costValue === null || expectedYield === null) {
      return;
    }

    const body: CropInputsBody = { costMode, costValue, expectedYield };
    if (plantedAcres !== undefined && plantedAcres !== null) {
      body.plantedAcres = plantedAcres;
    }

    submitting = true;
    save.setAttribute("disabled", "");
    errors.general.textContent = "Saving…";

    const result = await api.putCrop(crop, body);

    submitting = false;
    save.removeAttribute("disabled");

    if (result.ok) {
      errors.general.className = "form-message is-info";
      errors.general.textContent = "Saved.";
      return;
    }

    const responseBody = result.body as { fieldErrors?: FieldError[]; error?: string };
    if (result.status === 400 && Array.isArray(responseBody.fieldErrors)) {
      showFieldErrors(responseBody.fieldErrors);
      errors.general.className = "form-message is-error";
      errors.general.textContent = "Please fix the highlighted fields.";
      return;
    }

    errors.general.className = "form-message is-error";
    errors.general.textContent =
      result.status >= 500
        ? "The value was not saved. Please try again."
        : "Could not save. Please try again.";
  }

  return root;
}

/** A labeled control wrapper. */
function field(labelText: string, control: HTMLElement): HTMLElement {
  return el("div", { class: "field" }, [
    el("label", { class: "field-label", text: labelText }),
    control,
  ]);
}

/** A numeric text input configured for mobile decimal entry. */
function numberInput(name: string, ariaLabel: string, min: number, max: number): HTMLInputElement {
  return el("input", {
    type: "number",
    name,
    class: "field-input",
    attrs: {
      "aria-label": ariaLabel,
      inputmode: "decimal",
      step: "0.01",
      min: String(min),
      max: String(max),
    },
  }) as HTMLInputElement;
}

/** Parse a numeric input value; returns null when empty or non-numeric. */
function parseNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}
